import { Box, Button, Combobox, createListCollection, Flex, Portal, Tag } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { Field } from '@/components/ui/field';
import { capitalize } from '@/utils/capitalize';
import { CustomCellRendererProps } from '@ag-grid-community/react';
import { Plus } from 'lucide-react';
import { LinkModal } from './link-modal';
import { LinkDto, TemplateDto } from '@/lib/ServerApi';
import { useLoaderData, useRevalidator } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { palette } from './palette';

const showAddButton = (templateType: string | undefined, hasLinks: boolean) => {
    const hasProblemOrGoal = ['problem', 'goal'].includes(templateType || '');
    const hasPhysicianOrMedicationNoLinks = ['physician-order-subject', 'medication-purpose'].includes(templateType || '') && !hasLinks;

    return hasProblemOrGoal || hasPhysicianOrMedicationNoLinks;
};

const filterByType = (rowData: TemplateDto | undefined, d: TemplateDto) => {
    const typeMap: Record<string, string> = {
        problem: 'goal',
        goal: 'task',
        'medication-purpose': 'medication-side-effect',
        'physician-order-subject': 'physician-order-description',
    };

    const expectedType = typeMap[rowData?.templateType ?? ''];
    if (expectedType) {
        return d.templateType === expectedType;
    }
    return true;
};

export const TemplatesLinksRenderer = ({ value, data: rowData }: CustomCellRendererProps<TemplateDto>) => {
    const data = useLoaderData() as TemplateDto[];
    const [seeMore, setSeeMore] = useState(false);
    const [addMore, setAddMore] = useState(false);
    const links = !seeMore && value?.length ? value.slice(0, 3) : value;
    const [selectedLink, setSelectedLink] = useState<{ templateType: string | null; title: string | null } | null>(null);
    const { revalidate } = useRevalidator();

    const { mutate: linkTemplates } = useMutation<unknown, unknown, LinkDto>({
        mutationFn: async (data) => {
            return api.template.addLink(data);
        },
        onSettled: () => {
            revalidate();
        },
    });

    const { mutate: removeLink } = useMutation<unknown, unknown, LinkDto>({
        mutationFn: async ({ fromId, toId }) => {
            return api.template.removeLink(fromId, toId);
        },
        onSettled: () => {
            revalidate();
        },
    });

    const collection = useMemo(() => {
        return createListCollection({
            items: data
                .filter((d) => filterByType(rowData, d))
                .filter((d) => d.templateType !== rowData?.templateType)
                .filter((d) => !rowData?.links?.some((l) => l.id === d.id))
                .map((d) => ({ value: d.id, label: d.title })),
        });
    }, [data]);

    return !addMore ? (
        <>
            <Flex alignItems="center" height="100%" gap="2" flexWrap={seeMore ? 'wrap' : 'nowrap'} py={2}>
                {(links as TemplateDto[])?.map(({ id, templateType, title }) => (
                    <Tag.Root key={id} colorPalette={palette[templateType as keyof typeof palette]} _hover={{ cursor: 'pointer' }}>
                        <Tag.Label
                            onClick={() =>
                                setSelectedLink({ templateType, title })
                            }>{`${capitalize(title?.slice(0, 20))}${(title?.length ?? 0) > 20 ? '...' : ''}`}</Tag.Label>
                        <Tag.EndElement>
                            <Tag.CloseTrigger
                                onClick={() => removeLink({ fromId: rowData!.id!, toId: id! })}
                                _hover={{ cursor: 'pointer' }}
                            />
                        </Tag.EndElement>
                    </Tag.Root>
                ))}
                {showAddButton(rowData?.templateType, links.length) ? (
                    <Button
                        colorPalette="gray"
                        color="gray.700"
                        size="2xs"
                        variant="outline"
                        textDecoration="none"
                        onClick={() => setAddMore(true)}>
                        <Plus />
                    </Button>
                ) : undefined}
                {value?.length > 3 && !seeMore && (
                    <Button
                        colorPalette="gray"
                        color="gray.700"
                        size="2xs"
                        variant="outline"
                        textDecoration="none"
                        onClick={() => setSeeMore(!seeMore)}>
                        see {value?.length - 3} more
                    </Button>
                )}
                {seeMore && (
                    <Button
                        size="2xs"
                        colorPalette="gray"
                        color="gray.700"
                        variant="outline"
                        textDecoration="none"
                        onClick={() => setSeeMore(!seeMore)}>
                        see less
                    </Button>
                )}
            </Flex>
            {selectedLink && (
                <LinkModal
                    data={{ templateType: selectedLink.templateType!, title: selectedLink.title! }}
                    onClose={() => setSelectedLink(null)}
                />
            )}
        </>
    ) : (
        <Flex alignItems="center" gap="2">
            <Field flex="0 0 320px">
                <Combobox.Root
                    py="2"
                    width="320px"
                    collection={collection}
                    defaultOpen
                    onValueChange={(e) => {
                        const selectedTemplateId = e.value[0];
                        const rowTemplateId = rowData?.id;

                        if (selectedTemplateId && rowTemplateId) {
                            linkTemplates({ fromId: selectedTemplateId, toId: rowTemplateId });
                        }

                        setAddMore(false);
                    }}>
                    <Combobox.Control>
                        <Combobox.Input />
                        <Combobox.IndicatorGroup>
                            <Combobox.ClearTrigger />
                            <Combobox.Trigger />
                        </Combobox.IndicatorGroup>
                    </Combobox.Control>
                    <Portal>
                        <Combobox.Positioner>
                            <Combobox.Content>
                                <Combobox.Empty>
                                    {rowData?.templateType === 'goal'
                                        ? 'No Tasks / Interventions Found'
                                        : rowData?.templateType === 'problem'
                                          ? 'No Goals Found'
                                          : 'No Templates Found'}
                                </Combobox.Empty>
                                {collection.items.map((item) => {
                                    const { templateType } = data.find((d) => d.title === item.label) || {};

                                    return (
                                        <Combobox.Item key={item.value} item={item} _hover={{ cursor: 'pointer' }}>
                                            <Flex gap="2" alignItems="center">
                                                <Box
                                                    flex="0 0 auto"
                                                    borderRadius="100%"
                                                    width="5px"
                                                    height="5px"
                                                    lineHeight={0}
                                                    bgColor={`${palette[templateType as keyof typeof palette]}.400`}
                                                />
                                                {item.label}
                                                <Combobox.ItemIndicator />
                                            </Flex>
                                        </Combobox.Item>
                                    );
                                })}
                            </Combobox.Content>
                        </Combobox.Positioner>
                    </Portal>
                </Combobox.Root>
            </Field>
            <Button size="xs" variant="outline" textDecoration="none" onClick={() => setAddMore(false)}>
                Cancel
            </Button>
        </Flex>
    );
};
